import React, { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../_redux/students/StudentsAction";
import {
  DeleteButton,
  ExportButton,
  FormButton,
  copy,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  print,
  redirectToPages
} from "../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { StudentsFilter } from "./students-filter/StudentsFilter";
import { StudentsTable } from "./students-table/StudentsTable";
import { useStudentsUIContext } from "./StudentsUIContext";

export function StudentsCard() {
  // Students UI Context
  const studentsUIContext = useStudentsUIContext();
  const studentsUIProps = useMemo(() => {
    return {
      ids: studentsUIContext.ids,
      setIds: studentsUIContext.setIds,
      queryParams: studentsUIContext.queryParams,
      openNewStudentPage: studentsUIContext.openNewStudentPage,
      openRegistrationCancellationModal: studentsUIContext.openRegistrationCancellationModal
    };
  }, [studentsUIContext]);
    
  // Getting curret state of Student list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.students }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isAdditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);
  
  // Students Redux state
  const dispatch = useDispatch();

  /// Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Öğrenci Listesini Excele Aktarır */
  const exportStudentsToExcel = () => {
    dispatch(actions.getAllStudents(studentsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [15, 20, 20, 30, 30, 15, 15, 15];
      const columns = ["Kayıt Tarihi", "Öğrenci Numarası", "TC Kimlik No", "Adı", "Soyadı", "Cinsiyeti", "Şube"];
      
      entities.forEach(entity => rows.push([
        entity.registrationDate,
        entity.studentNumber,
        entity.identityCard.tcKimlikNo,
        entity.identityCard.name,
        entity.identityCard.surname,
        entity.sexName,
        entity.section
      ]));
      
      const filename = studentsUIProps.queryParams.term + " - " + (studentsUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğrenci Listesi";
      const sheetname = "Öğrenci Listesi";
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  /** Belli Bir Öğrenci Listesini PDF e Aktarır */
  const exportStudentsToPDF = () => {
    dispatch(actions.getAllStudents(studentsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "Kayıt Tarihi",
          rows: entities.map(entity => entity.registrationDate)
        },
        {
          header: "Öğrenci Numarası",
          rows: entities.map(entity => entity.studentNumber)
        },
        {
          header: "TC Kimlik No",
          rows: entities.map(entity => entity.identityCard.tcKimlikNo)
        },
        {
          header: "Adı",
          rows: entities.map(entity => entity.identityCard.name)
        },
        {
          header: "Soyadı",
          rows: entities.map(entity => entity.identityCard.surname)
        },
        {
          header: "Cinsiyeti",
          rows: entities.map(entity => entity.sexName)
        },
        {
          header: "Şube",
          rows: entities.map(entity => entity.section)
        }
      ];
  
      const filename = studentsUIProps.queryParams.term + " - " + (studentsUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğrenci Listesi";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Öğrenci Listesini Yazdırma Penceresini Açar */
  const printStudents = () => {
    dispatch(actions.getAllStudents(studentsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = studentsUIProps.queryParams.term + " - " + (studentsUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğrenci Listesi";
      const properties = ["Kayıt Tarihi", "Öğrenci Numarası", "TC Kimlik No", "Adı", "Soyadı", "Cinsiyeti", "Şube"];
  
      entities.forEach(entity => {
        data.push({
          "Kayıt Tarihi": entity.registrationDate,
          "Öğrenci Numarası": entity.studentNumber,
          "TC Kimlik No": entity.identityCard.tcKimlikNo,
          "Adı": entity.identityCard.name,
          "Soyadı": entity.identityCard.surname,
          "Cinsiyeti": entity.sexName,
          "Şube": entity.section
        });
      });
  
      print({ data, properties, header });
    });    
  }

  /** Belli Bir Öğrenci Listesini Panoya Kopyalar */
  const copyStudents = () => {
    dispatch(actions.getAllStudents(studentsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["Kayıt Tarihi", "Öğrenci Numarası", "TC Kimlik No", "Adı", "Soyadı", "Cinsiyeti", "Şube"];
      const header = studentsUIProps.queryParams.term + " - " + (studentsUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğrenci Listesi";
      entities.forEach(entity => rows.push([
        entity.registrationDate,
        entity.studentNumber,
        entity.identityCard.tcKimlikNo,
        entity.identityCard.name,
        entity.identityCard.surname,
        entity.sexName,
        entity.section
      ]));
      copy({ header, columns, rows });
    });
  }

  const title = <>Öğrenci Listesi{studentsUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{studentsUIProps.ids.length}</span>]</> :
                  ""}
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isAdditionAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportStudentsToExcel}
                exportToPDF={exportStudentsToPDF}
                print={printStudents}
                copy={copyStudents}
              />
            }
            {
              isAdditionAuthorized &&
              <FormButton 
                path="Navigation/Plus"
                title="Yeni Öğrenci"
                method={studentsUIProps.openNewStudentPage}
              />
            }
            {
              isDeletionAuthorized &&
              <DeleteButton 
                title="Kayıtları İptal Et"
                method={() => studentsUIProps.openRegistrationCancellationModal(studentsUIProps.queryParams.term, studentsUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }        
      </CardHeader>
      <CardBody>
        <StudentsFilter />
        <StudentsTable />
      </CardBody>
    </Card>
  );
}